import { z } from 'zod';
import { restricted } from './redaction.js';
/**
 * Visits and the conflict engine at write time (slice-03 §6, §14.2–§14.3).
 *
 * ## Instants on the wire, the zone alongside
 *
 * A visit is written as two UTC-offset instants. The server derives the local
 * date and times from the service location's zone — it is never told them, so
 * it never has a naked local datetime to misread. Responses carry the instants,
 * the zone, and the local date and times already worked out, so a client
 * renders them rather than converting them (R-1e).
 *
 * ## Overrides are named, one per rule, each with a reason
 *
 * A write that a rule would refuse carries `overrides: [{ rule, reason }]`.
 * Nothing is overridden by holding a permission alone: the person names the
 * rule they read and says why, and that sentence is what the weekly override
 * report shows. A blanket "override everything" flag does not exist.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-06 — four digits, two, two.');
const instant = z
    .string()
    .datetime({ offset: true, message: 'Use a full date and time with its offset.' });
/** The longest a single visit may be. A longer booking is two visits. */
export const MAX_VISIT_MINUTES = 24 * 60;
export const visitStatusSchema = z.enum([
    'SCHEDULED',
    'IN_PROGRESS',
    'COMPLETED',
    'NEEDS_REVIEW',
    'VERIFIED',
    'REJECTED',
    'CANCELLED',
    'MISSED',
]);
/** Badges, orthogonal to status — never a state of the machine. */
export const visitRiskFlagSchema = z.enum(['CALLOUT', 'AT_RISK', 'LATE_CANCEL', 'UNSCHEDULED']);
export const cancellationReasonSchema = z.enum([
    'CLIENT_REQUEST',
    'CLIENT_HOSPITALISED',
    'CLIENT_UNAVAILABLE',
    'SERVICE_ENDED',
    'AGENCY_ERROR',
    'WEATHER',
    'OTHER',
]);
// ---------------------------------------------------------------------------
// The conflict engine's answer
// ---------------------------------------------------------------------------
export const conflictRuleSchema = z.number().int().min(1).max(15);
export const conflictFindingSchema = z.object({
    rule: conflictRuleSchema,
    code: z.string(),
    outcome: z.enum(['BLOCK', 'WARN', 'PASS', 'UNKNOWN', 'NOT_APPLICABLE']),
    message: z.string(),
    /** The permission that may book past it. Null: nobody can. */
    overridableBy: z.string().nullable(),
});
export const conflictCheckSchema = z.object({
    /** All fifteen, in rule order. */
    findings: z.array(conflictFindingSchema),
    /** What stops this write for this person, after the overrides they may make. */
    refusals: z.array(conflictFindingSchema),
    /** Blocking findings this person may override — each needs a reason. */
    overridable: z.array(conflictFindingSchema),
    warnings: z.array(conflictFindingSchema),
    /** True when nothing refuses and every overridable finding has a reason. */
    canSave: z.boolean(),
});
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
export const overrideRequestSchema = z.object({
    rule: conflictRuleSchema,
    reason: z
        .string()
        .trim()
        .min(1, 'Say why this rule should not apply here. It goes on the override report.')
        .max(2_000),
});
const overrides = z
    .array(overrideRequestSchema)
    .max(15)
    .default([])
    .refine((list) => new Set(list.map((entry) => entry.rule)).size === list.length, {
    message: 'Give one reason per rule.',
});
const spanIsSane = (body) => {
    if (body.startsAt === undefined || body.endsAt === undefined)
        return true;
    const minutes = (Date.parse(body.endsAt) - Date.parse(body.startsAt)) / 60_000;
    return minutes > 0 && minutes <= MAX_VISIT_MINUTES;
};
const spanMessage = {
    path: ['endsAt'],
    message: 'A visit ends after it starts, and lasts no more than 24 hours.',
};
export const createVisitSchema = z
    .object({
    clientId: z.string().uuid(),
    /** Null: an open shift. */
    caregiverId: z.string().uuid().nullable(),
    serviceCodeId: z.string().uuid(),
    /** The service location — one of the client's addresses. */
    clientAddressId: z.string().uuid(),
    /** Omitted: the authorization covering this client, service and day is found. */
    authorizationId: z.string().uuid().nullish(),
    startsAt: instant,
    endsAt: instant,
    overrides,
})
    .refine(spanIsSane, spanMessage);
/** The same question without writing: what would the engine say? */
export const checkVisitSchema = z
    .object({
    /** Set when checking an edit, so the visit is not compared with itself. */
    visitId: z.string().uuid().nullish(),
    clientId: z.string().uuid(),
    caregiverId: z.string().uuid().nullable(),
    serviceCodeId: z.string().uuid(),
    clientAddressId: z.string().uuid(),
    authorizationId: z.string().uuid().nullish(),
    startsAt: instant,
    endsAt: instant,
    overrides,
})
    .refine(spanIsSane, spanMessage);
/**
 * An edit re-runs every rule (§14.3) — moving a visit two hours later re-runs
 * travel, availability and overtime, not just the overlap.
 *
 * `version` is the one the editor saw. Two people editing the same visit: the
 * second is told it changed, rather than silently undoing the first.
 */
export const updateVisitSchema = z
    .object({
    version: z.number().int().min(0),
    caregiverId: z.string().uuid().nullable().optional(),
    serviceCodeId: z.string().uuid().optional(),
    clientAddressId: z.string().uuid().optional(),
    authorizationId: z.string().uuid().nullable().optional(),
    startsAt: instant.optional(),
    endsAt: instant.optional(),
    overrides,
})
    .refine(spanIsSane, spanMessage);
export const cancelVisitSchema = z.object({
    version: z.number().int().min(0),
    reasonCode: cancellationReasonSchema,
    note: z.string().trim().max(2_000).nullish(),
});
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
export const visitListQuerySchema = z
    .object({
    from: isoDate,
    to: isoDate,
    clientId: z.string().uuid().optional(),
    caregiverId: z.string().uuid().optional(),
    /** Only open shifts. The literal strings, so `false` cannot coerce to true. */
    open: z.enum(['true', 'false']).optional(),
    includeCancelled: z.enum(['true', 'false']).optional(),
})
    .refine((query) => query.to >= query.from, {
    path: ['to'],
    message: 'The last day can’t be before the first.',
});
export const visitSummarySchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    clientName: z.string(),
    caregiverId: z.string().uuid().nullable(),
    caregiverName: z.string().nullable(),
    serviceCodeId: z.string().uuid(),
    serviceCode: z.string(),
    status: visitStatusSchema,
    riskFlags: z.array(visitRiskFlagSchema),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    /** The service location's IANA zone. The local fields below are in it. */
    timezone: z.string(),
    localDate: isoDate,
    localStartTime: z.string(),
    localEndTime: z.string(),
    /** Ends on the day after `localDate`. */
    crossesMidnight: z.boolean(),
    scheduledMinutes: z.number().int(),
    version: z.number().int(),
});
export const visitListSchema = z.object({ visits: z.array(visitSummarySchema) });
export const visitTaskSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** Care plan instructions can name medication and conditions. */
    instructions: restricted(z.string().nullable(), 'client:read_phi'),
    isRequired: z.boolean(),
    result: z.string(),
});
export const visitOverrideSchema = z.object({
    id: z.string().uuid(),
    rule: z.number().int(),
    code: z.string(),
    reason: z.string(),
    overriddenByName: z.string().nullable(),
    createdAt: z.string().datetime(),
});
/** One line of a visit's history: who did what, and when (slice-03 §8). */
export const visitHistoryEntrySchema = z.object({
    id: z.string().uuid(),
    /** The audit action, e.g. `visit.called_out`. */
    action: z.string(),
    /** The same thing in words a coordinator reads. */
    summary: z.string(),
    /** Null: the platform did it — a pattern materialising, a job. */
    actorName: z.string().nullable(),
    reason: z.string().nullable(),
    occurredAt: z.string().datetime(),
});
export const visitDetailSchema = visitSummarySchema.extend({
    clientAddressId: z.string().uuid(),
    /** The service location on one line. A client's home address is PHI. */
    address: restricted(z.string().nullable(), 'client:read_phi'),
    authorizationId: z.string().uuid().nullable(),
    shiftPatternId: z.string().uuid().nullable(),
    cancellationReasonCode: z.string().nullable(),
    cancelledAt: z.string().datetime().nullable(),
    tasks: z.array(visitTaskSchema),
    overrides: z.array(visitOverrideSchema),
    /** Oldest first. */
    history: z.array(visitHistoryEntrySchema),
});
/** A write's answer: the visit, and the warnings it was saved with. */
export const visitWriteResultSchema = z.object({
    visit: visitDetailSchema,
    warnings: z.array(conflictFindingSchema),
});
// ---------------------------------------------------------------------------
// The schedule board (slice-03 §8)
// ---------------------------------------------------------------------------
export const scheduleBoardQuerySchema = z.object({
    /** Any day in the week wanted. Omitted: the agency's today (R-1e). */
    weekOf: isoDate.optional(),
});
export const boardCaregiverSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** ACTIVE, or whatever a caregiver with a visit this week now is. */
    status: z.string(),
});
/**
 * A week of the board in one answer: the rows, the visits, and the agency's
 * calendar to lay them out on. The client draws days from `days` and places a
 * card by its `localDate` — it never works out a day itself.
 */
export const scheduleBoardSchema = z.object({
    weekStart: isoDate,
    weekEnd: isoDate,
    /** Sunday to Saturday, in the agency's zone. */
    days: z.array(isoDate).length(7),
    /** The agency's today, so "today" is highlighted on New York's day, not UTC's. */
    today: isoDate,
    timezone: z.string(),
    /**
     * Every ACTIVE caregiver, plus anyone not ACTIVE who still holds a visit in
     * the week — a row must never vanish while it has work on it. Sorted by name.
     */
    caregivers: z.array(boardCaregiverSchema),
    visits: z.array(visitSummarySchema),
});
// ---------------------------------------------------------------------------
// The weekly override report (§14.3)
// ---------------------------------------------------------------------------
export const overrideReportQuerySchema = z.object({
    /** Any day in the week wanted; the week is Sunday to Saturday in the agency's zone. */
    weekOf: isoDate.optional(),
});
export const overrideReportSchema = z.object({
    weekStart: isoDate,
    weekEnd: isoDate,
    timezone: z.string(),
    overrides: z.array(z.object({
        id: z.string().uuid(),
        visitId: z.string().uuid(),
        visitStartsAt: z.string().datetime(),
        clientName: z.string(),
        caregiverName: z.string().nullable(),
        rule: z.number().int(),
        code: z.string(),
        reason: z.string(),
        overriddenByName: z.string().nullable(),
        createdAt: z.string().datetime(),
    })),
    /** Counts by rule code, so a rule overridden every day stands out. */
    byCode: z.array(z.object({ code: z.string(), count: z.number().int() })),
});
//# sourceMappingURL=visit.js.map